import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { Search, TrendingUp, TrendingDown, X, Download, BarChart3, Loader2 } from 'lucide-react';
import { Box, Typography, Button, IconButton, useTheme } from '@mui/material';
import { searchStockName } from '../../../api/api';
import { getStockComparison, getComparisonNarrative } from '../../../api/stocks_api';
import { refreshToken } from '../../../api/auth_api';
import { useNavigate } from 'react-router-dom';
import TimeoutSnackbar from '../../../components/common/TimeoutSnackbar';
const market = sessionStorage.getItem("selectedMarket") ?? "US";

const PRESETS = {
  TR: {
    tech: ['LOGO.IS', 'VESBE.IS', 'KAREL.IS'],
    auto: ['FROTO.IS', 'TOASO.IS', 'OTKAR.IS'],
  },
  DEFAULT: {
    tech: ['AAPL', 'MSFT', 'GOOGL'],
    auto: ['TSLA', 'F', 'GM'],
  },
};

const SEARCH_PLACEHOLDER = {
  TR: 'Search or add tickers to compare (e.g. LOGO.IS, FROTO.IS, THYAO.IS)...',
  DEFAULT: 'Search or add tickers to compare (e.g. AMZN, META, GOOGL)...',
};

const getMarket = () => sessionStorage.getItem('selectedMarket') ?? 'US';
const getPresets = () => PRESETS[getMarket()] ?? PRESETS.DEFAULT;
const getPlaceholder = () => SEARCH_PLACEHOLDER[getMarket()] ?? SEARCH_PLACEHOLDER.DEFAULT;

const Comparison = () => {
  const theme = useTheme();
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [tickerInput, setTickerInput] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [companyData, setCompanyData] = useState({});
  const [industryAvg, setIndustryAvg] = useState({});
  const [industrySector, setIndustrySector] = useState('');
  const [hasIndustryAvg, setHasIndustryAvg] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [comparedTickers, setComparedTickers] = useState([]);


  // Search states
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const searchRef = useRef(null);
  const [redirecting, setRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const navigate = useNavigate();

  const [narrative, setNarrative] = useState('');
  const [narrativeLoading, setNarrativeLoading] = useState(false);

  const TypingNarrative = ({ text, speed = 8 }) => {
    const [displayed, setDisplayed] = useState('');
    const [done, setDone] = useState(false);
    const idxRef = useRef(0);
    const rafRef = useRef(null);
    const lastTimeRef = useRef(null);

    useEffect(() => {
      if (!text) return;
      setDisplayed('');
      setDone(false);
      idxRef.current = 0;
      lastTimeRef.current = null;

      const step = (timestamp) => {
        if (!lastTimeRef.current) lastTimeRef.current = timestamp;
        const elapsed = timestamp - lastTimeRef.current;

        if (elapsed >= speed) {
          const charsToAdd = Math.max(1, Math.floor(elapsed / speed));
          idxRef.current = Math.min(idxRef.current + charsToAdd, text.length);
          setDisplayed(text.slice(0, idxRef.current));
          lastTimeRef.current = timestamp;
        }

        if (idxRef.current < text.length) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          setDone(true);
        }
      };

      rafRef.current = requestAnimationFrame(step);
      return () => cancelAnimationFrame(rafRef.current);
    }, [text, speed]);

    if (!text) return null;

    if (done) {
      /* ── DONE: render full text prop directly — guaranteed complete ── */
      return (
        <Typography
          variant="paragraph"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.7,
            whiteSpace: 'pre-line',
            display: 'inline',
          }}
        >
          {text}
        </Typography>
      );
    }

    /* ── TYPING: plain <pre>, nothing parsed or swallowed mid-stream ── */
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'inherit',
          fontSize: 'var(--font-size-base)',
          lineHeight: 1.7,
          color: 'text.secondary',
          display: 'inline',
        }}
      >
        {displayed}
        {/* Blinking cursor */}
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: '2px',
            height: '1em',
            bgcolor: 'primary.main',
            ml: '2px',
            verticalAlign: 'text-bottom',
            animation: 'cursorBlink 0.7s step-start infinite',
            '@keyframes cursorBlink': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0 },
            },
          }}
        />
      </Box>
    );
  };

  const fetchNarrative = async (stocksArray, industryAvgData) => {
    setNarrativeLoading(true);

    try {
      // Structure industry_avg correctly: use metrices if available, otherwise null
      let industryAvgPayload = null;
      if (industryAvgData && industryAvgData.metrices) {
        industryAvgPayload = industryAvgData.metrices;
      }

      const payload = {
        stocks: stocksArray.map(stock => ({
          ticker: stock.ticker,
          p_e_ratio: stock.p_e_ratio,
          operating_profit_margin: stock.operating_profit_margin,
          debt_to_equity: stock.debt_to_equity,
          return_on_equity: stock.return_on_equity,
        })),
        industry_avg: industryAvgPayload,
      };

      const res = await getComparisonNarrative(payload);

      if (res.success && res.data) {
        setNarrative(res.data.analysis || res.data.narrative || '');
      } else {
        setNarrative('Narrative generation failed.');
      }
    } catch (error) {
      console.error(error);
      setNarrative('Narrative generation failed.');
    } finally {
      setNarrativeLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!tickerInput.trim()) {
      setSearchResults([]);
      setSearchError('');
      return;
    }

    const delay = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError('');

      try {
        const res = await searchStockName(tickerInput.trim());

        if (res.authExpired) {
          const refreshed = await refreshToken();
          if (refreshed.success) {
            const retryRes = await searchStockName(tickerInput.trim());
            if (retryRes.success && Array.isArray(retryRes.data)) {
              setSearchResults(retryRes.data);
              return;
            }
          }
          setRedirecting(true);
          return;
        }

        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          setSearchResults(res.data);
        } else {
          setSearchResults([]);
        }
      } catch {
        setSearchError('Search failed');
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [tickerInput]);

  // Close search results on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Redirect countdown
  useEffect(() => {
    if (!redirecting) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          sessionStorage.clear();
          navigate('/login', { replace: true });
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [redirecting, navigate]);

  const handleAddTicker = (ticker) => {
    const upperTicker = ticker.toUpperCase();
    if (upperTicker && !selectedTickers.includes(upperTicker)) {
      setSelectedTickers([...selectedTickers, upperTicker]);
      setTickerInput('');
      setSearchResults([]);
    }
  };

  const handleDeleteTicker = (ticker) => {
    const updatedTickers = selectedTickers.filter((t) => t !== ticker);

    setSelectedTickers(updatedTickers);
    setComparedTickers([]);
    if (updatedTickers.length === 0) {
      setShowAnalysis(false);
      setCompanyData({});
      setIndustryAvg({});
      setHasIndustryAvg(false);
      setIndustrySector('');
      return;
    }

    const tickersString = updatedTickers.join(',');
    fetchComparison(tickersString);
  };

  const handleCompare = async () => {
    if (selectedTickers.length === 0) {
      setApiError('Please add at least 1 ticker to compare');
      return;
    }

    const tickersString = selectedTickers.join(',');
    fetchComparison(tickersString);
  };

  const processApiData = (data) => {
    const companies = {};
    const avgData = {};

    const responseData = data.response_data || data;

    if (responseData.stocks && Array.isArray(responseData.stocks)) {
      responseData.stocks.forEach(stock => {
        companies[stock.ticker] = {
          name: stock.company_name || stock.ticker,
          peRatio: parseFloat(stock.p_e_ratio) || 0,
          debtEquity: parseFloat(stock.debt_to_equity) || 0,
          roe: stock.return_on_equity ? parseFloat(stock.return_on_equity) * 100 : 0,
          currentRatio: stock.current_ratio || 0,
          operatingMargin: stock.operating_profit_margin ? parseFloat(stock.operating_profit_margin) * 100 : 0,
        };
      });
    }

    const hasIndustryData = responseData.industry_avg !== null && responseData.industry_avg !== undefined;
    setHasIndustryAvg(hasIndustryData);

    if (hasIndustryData && responseData.industry_avg.metrices) {
      const metrics = responseData.industry_avg.metrices;
      avgData.peRatio = parseFloat(metrics.avg_p_e_ratio) || 0;
      avgData.debtEquity = parseFloat(metrics.avg_debt_to_equity) || 0;
      avgData.roe = metrics.avg_return_on_equity ? parseFloat(metrics.avg_return_on_equity) * 100 : 0;
      avgData.currentRatio = metrics.avg_current_ratio || 0;
      avgData.operatingMargin = metrics.avg_operating_profit_margin ? parseFloat(metrics.avg_operating_profit_margin) * 100 : 0;

      setIndustrySector(responseData.industry_avg.sector || '');
    } else {
      setIndustrySector('');
      const stockCount = Object.keys(companies).length;
      if (stockCount > 0) {
        avgData.peRatio = Object.values(companies).reduce((sum, c) => sum + c.peRatio, 0) / stockCount;
        avgData.debtEquity = Object.values(companies).reduce((sum, c) => sum + c.debtEquity, 0) / stockCount;
        avgData.roe = Object.values(companies).reduce((sum, c) => sum + c.roe, 0) / stockCount;
        avgData.currentRatio = Object.values(companies).reduce((sum, c) => sum + c.currentRatio, 0) / stockCount;
        avgData.operatingMargin = Object.values(companies).reduce((sum, c) => sum + c.operatingMargin, 0) / stockCount;
      }
    }

    setCompanyData(companies);
    setIndustryAvg(avgData);
  };

  const handleTryTechGiants = () => {
    const tickers = getPresets().tech;
    setSelectedTickers(tickers);
    setTimeout(() => fetchComparison(tickers.join(',')), 100);
  };

  const handleTryAutoSector = () => {
    const tickers = getPresets().auto;
    setSelectedTickers(tickers);
    setTimeout(() => fetchComparison(tickers.join(',')), 100);
  };

  const fetchComparison = async (tickersString) => {
    setLoading(true);
    setApiError('');

    try {
      const res = await getStockComparison(tickersString);

      if (res.authExpired) {
        const refreshed = await refreshToken();
        if (refreshed.success) {
          const retryRes = await getStockComparison(tickersString);
          if (retryRes.success) {
            processApiData(retryRes.data);
            setShowAnalysis(true);
            setComparedTickers([...selectedTickers]);
            return;
          }
        }
        setRedirecting(true);
        return;
      }

      if (res.success && res.data) {
        processApiData(res.data);
        setShowAnalysis(true);
        setComparedTickers([...selectedTickers]);

        const responseData = res.data.response_data || res.data;

        fetchNarrative(
          responseData.stocks || [],
          responseData.industry_avg || {}
        );
      } else {
        setApiError('Failed to fetch comparison data');
      }
    } catch (error) {
      console.error('Comparison error:', error);
      setApiError('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const avgLabel = hasIndustryAvg ? 'Industry Avg' : 'Average';

  const barChartData = [
    {
      name: 'P/E Ratio',
      ...Object.fromEntries(selectedTickers.map(t => [t, companyData[t]?.peRatio || 0])),
      [avgLabel]: industryAvg.peRatio || 0,
    },
    {
      name: 'Debt/Equity',
      ...Object.fromEntries(selectedTickers.map(t => [t, companyData[t]?.debtEquity || 0])),
      [avgLabel]: industryAvg.debtEquity || 0,
    },
    {
      name: 'ROE (%)',
      ...Object.fromEntries(selectedTickers.map(t => [t, companyData[t]?.roe || 0])),
      [avgLabel]: industryAvg.roe || 0,
    },
    {
      name: 'Operating Margin (%)',
      ...Object.fromEntries(selectedTickers.map(t => [t, companyData[t]?.operatingMargin || 0])),
      [avgLabel]: industryAvg.operatingMargin || 0,
    },
  ];

  const radarData = [
    {
      metric: 'P/E Ratio',
      ...Object.fromEntries(selectedTickers.map(t => [
        t,
        industryAvg.peRatio ? (companyData[t]?.peRatio / industryAvg.peRatio) * 100 : 0
      ])),
      [avgLabel]: 100,
    },
    {
      metric: 'Operating Margin',
      ...Object.fromEntries(selectedTickers.map(t => [
        t,
        industryAvg.operatingMargin ? (companyData[t]?.operatingMargin / industryAvg.operatingMargin) * 100 : 0
      ])),
      [avgLabel]: 100,
    },
    {
      metric: 'ROE (%)',
      ...Object.fromEntries(selectedTickers.map(t => [
        t,
        industryAvg.roe ? (companyData[t]?.roe / industryAvg.roe) * 100 : 0
      ])),
      [avgLabel]: 100,
    },
    {
      metric: 'Debt/Equity',
      ...Object.fromEntries(selectedTickers.map(t => [
        t,
        industryAvg.debtEquity ? (companyData[t]?.debtEquity / industryAvg.debtEquity) * 100 : 0
      ])),
      [avgLabel]: 100,
    },
  ];

  const metrics = [
    { label: 'P/E Ratio', key: 'peRatio' },
    { label: 'Debt/Equity', key: 'debtEquity' },
    { label: 'ROE (%)', key: 'roe' },
    { label: 'Operating Margin (%)', key: 'operatingMargin' },
  ];

  const colors = ['#60a5fa', '#ec4899', '#fbbf24', '#8b5cf6', '#10b981', '#f97316'];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
        px: { xs: 2, md: 4 },
        py: 3.5,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection:"column" , mb: 3.5 }}>
        {/* <Box style={{ display: "flex", flexDirection: "column" }}> */}
        <Typography
          variant="title"
          fontWeight="var(--font-weight-bold)"
          sx={{ color: 'var(--color-text-primary)', display: 'block' }}
          letterSpacing="-0.02em"
          mb={0.5}
        >
          Comparative Ratio Analysis
        </Typography>
        <Typography variant="subtitle" fontWeight="var(--font-weight-normal)" sx={{ color: 'var(--color-text-muted)' }}>
          Institutional-grade benchmarking against peers and industry standards.
        </Typography>
        {/* </Box> */}
        {/* <Button
          variant="outlined"
          startIcon={<Download size={14} />}
          sx={{
            border: `1px solid ${theme.palette.border.secondary}`,
            color: theme.palette.text.secondary,
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              border: `1px solid ${theme.palette.border.tertiary}`,
              bgcolor: 'transparent'
            }
          }}
        >
          Export Report
        </Button> */}
      </Box>

      {market === "ALL" ? (<>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            p: 3,
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--color-info)",
            bgcolor: "var(--color-info-light)",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "var(--radius-md)",
              bgcolor: "var(--color-info)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: "var(--font-weight-black)", fontSize: "var(--font-size-lg)" }}>
              !
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="tableHeader" fontWeight="var(--font-weight-bold)" sx={{ color: "var(--color-text-primary)" }} mb={0.5}>
              Market Selection Required
            </Typography>
            <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)" }}>
              The Stock Screener is not available in <strong>All Markets</strong> mode. Please select a specific market (e.g. <strong>US</strong> or <strong>TR</strong>) from the market selector to access the screener.
            </Typography>
          </Box>
        </Box>
      </>) : (<>

        {/* Search Bar with Autocomplete */}
        <Box
          ref={searchRef}
          sx={{
            position: 'relative',
            bgcolor: theme.palette.surface.main,
            backdropFilter: 'blur(16px)',
            border: `1px solid ${theme.palette.border.primary}`,
            borderRadius: 2,
            zIndex: 29,
            p: 1.5,
            mb: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Search size={16} color={theme.palette.text.muted} />
            <input
              type="text"
              // placeholder="Search or add tickers to compare (e.g. AMZN, META, GOOGL)..."
              placeholder={getPlaceholder()}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'revert',
                color: theme.palette.text.primary,
                fontSize: '16px',
                fontFamily: theme.typography.fontFamily,
              }}
              value={tickerInput}
              onChange={(e) => setTickerInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && tickerInput.trim()) {
                  handleAddTicker(tickerInput);
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleCompare}
              disabled={
                loading ||
                selectedTickers.length === 0 ||
                (showAnalysis && JSON.stringify(selectedTickers) === JSON.stringify(comparedTickers))
              }
              startIcon={loading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.text.inverse,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                  opacity: 0.9
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                  color: theme.palette.text.inverse
                }
              }}
            >
              {loading
                ? 'Loading...'
                : showAnalysis && JSON.stringify(selectedTickers) === JSON.stringify(comparedTickers)
                  ? 'Comparison Done ✓'
                  : 'Compare Now'}
            </Button>
          </Box>

          {/* Search Results Dropdown */}
          {searchLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                mt: 1,
                width: '100%',
                bgcolor: theme.palette.background.secondary,
                border: `1px solid ${theme.palette.border.secondary}`,
                borderRadius: 1.5,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                p: 1.5,
                zIndex: 9999
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: theme.palette.text.muted }}>
                <Loader2 size={14} className="animate-spin" />
                <Typography sx={{ fontSize: '12px' }}>Searching...</Typography>
              </Box>
            </Box>
          )}

          {!searchLoading && searchResults.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                mt: 1,
                width: '100%',
                bgcolor: theme.palette.background.secondary,
                border: `1px solid ${theme.palette.border.secondary}`,
                borderRadius: 1.5,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                zIndex: 9999,
                overflow: 'hidden'
              }}
            >
              <Box sx={{ maxHeight: '256px', overflowY: 'auto' }}>
                {searchResults.map((item, index) => (
                  <Box
                    key={item.ticker}
                    onClick={() => handleAddTicker(item.ticker)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      borderBottom: index < searchResults.length - 1 ? `1px solid ${theme.palette.border.secondary}` : 'none',
                      '&:hover': {
                        bgcolor: theme.palette.surface.hover
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.text.primary, width: '80px' }}>
                        {item.ticker}
                      </Typography>
                      <Typography sx={{ fontSize: '16px', color: theme.palette.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.company_name}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {!searchLoading && searchError && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                mt: 1,
                width: '100%',
                bgcolor: theme.palette.background.secondary,
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: 1.5,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                p: 1.5,
                zIndex: 9999,
                fontSize: '12px',
                color: theme.palette.error.main
              }}
            >
              {searchError}
            </Box>
          )}
        </Box>

        {/* Error Message */}
        {apiError && (
          <Box
            sx={{
              bgcolor: `${theme.palette.error.main}10`,
              border: `1px solid ${theme.palette.error.main}50`,
              borderRadius: 1.5,
              p: 1.5,
              mb: 2.5,
              fontSize: '14px',
              color: theme.palette.error.main
            }}
          >
            {apiError}
          </Box>
        )}

        {/* Selected Ticker Chips */}
        {selectedTickers.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
              mb: 2.5,
              p: 2,
              bgcolor: `${theme.palette.surface.main}40`,
              border: `1px solid ${theme.palette.border.primary}`,
              borderRadius: 2
            }}
          >
            <Typography sx={{ fontSize: "var(--font-size-sm)", color: theme.palette.text.muted, fontWeight: 500 }}>
              Selected:
            </Typography>
            {selectedTickers.map((ticker) => (
              <Box
                key={ticker}
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: `${theme.palette.primary.main}20`,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: theme.palette.primary.light,
                  borderRadius: 1.5,
                  fontSize: '12px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: `${theme.palette.primary.main}30`
                  }
                }}
              >
                {ticker}
                <IconButton
                  size="small"
                  onClick={() => handleDeleteTicker(ticker)}
                  sx={{
                    p: 0,
                    color: 'inherit',
                    '&:hover': {
                      color: theme.palette.primary.main
                    }
                  }}
                >
                  <X size={14} />
                </IconButton>
              </Box>
            ))}
            <Button
              onClick={() => {
                setSelectedTickers([]);
                setShowAnalysis(false);
                setCompanyData({});
                setIndustryAvg({});
                setHasIndustryAvg(false);
                setIndustrySector('');
              }}
              sx={{
                color: theme.palette.text.muted,
                fontSize: "var(--font-size-sm)",
                textDecoration: 'underline',
                textTransform: 'none',
                minWidth: 'auto',
                p: 0,
                ml: 1,
                '&:hover': {
                  color: theme.palette.text.secondary,
                  bgcolor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              Clear all
            </Button>
          </Box>
        )}

        {/* Empty State */}
        {!showAnalysis && (
          <Box
            sx={{
              border: `2px dashed ${theme.palette.border.primary}`,
              borderRadius: 2,
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px'
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                bgcolor: `${theme.palette.background.secondary}50`,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <BarChart3 size={32} color={theme.palette.border.tertiary} />
            </Box>
            <Typography sx={{ fontSize: '24px', fontWeight: 600, mb: 1, color: theme.palette.text.secondary }}>
              Analysis Pending
            </Typography>
            <Typography sx={{ color: theme.palette.text.muted, textAlign: 'center', fontSize: '16px', mb: 3, maxWidth: '448px' }}>
              Search and add tickers above, then click "Compare Now" to generate a deep-dive ratio benchmarking report.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleTryTechGiants}
                sx={{
                  bgcolor: theme.palette.surface.main,
                  color: theme.palette.text.primary,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: theme.palette.surface.hover
                  }
                }}
              >
                Try Tech Giants
              </Button>
              <Button
                variant="contained"
                onClick={handleTryAutoSector}
                sx={{
                  bgcolor: theme.palette.surface.main,
                  color: theme.palette.text.primary,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: theme.palette.surface.hover
                  }
                }}
              >
                Try Auto Sector
              </Button>
            </Box>
          </Box>
        )}

        {/* Analysis Content */}
        {showAnalysis && (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: `1px solid ${theme.palette.border.primary}`, mb: 2.5 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Button
                  onClick={() => setActiveTab(0)}
                  sx={{
                    pb: 1.5,
                    px: 0.5,
                    fontWeight: 600,
                    fontSize: '14px',
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    color: activeTab === 0 ? theme.palette.primary.light : theme.palette.text.muted,
                    borderBottom: activeTab === 0 ? `2px solid ${theme.palette.primary.light}` : 'none',
                    borderRadius: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: activeTab === 0 ? theme.palette.primary.light : theme.palette.text.secondary
                    }
                  }}
                >
                  Visual Analysis
                </Button>
                <Button
                  onClick={() => setActiveTab(1)}
                  sx={{
                    pb: 1.5,
                    px: 0.5,
                    fontWeight: 600,
                    fontSize: '14px',
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    color: activeTab === 1 ? theme.palette.primary.light : theme.palette.text.muted,
                    borderBottom: activeTab === 1 ? `2px solid ${theme.palette.primary.light}` : 'none',
                    borderRadius: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: activeTab === 1 ? theme.palette.primary.light : theme.palette.text.secondary
                    }
                  }}
                >
                  Numerical Matrix
                </Button>
              </Box>
            </Box>

            {/* Visual Analysis Tab */}
            {activeTab === 0 && (
              <Box>
                {/* Single Stock vs Industry Message */}
                {selectedTickers.length === 1 && hasIndustryAvg && (
                  <Box
                    sx={{
                      mb: 2.5,
                      bgcolor: `${theme.palette.primary.main}10`,
                      border: `1px solid ${theme.palette.primary.main}30`,
                      borderRadius: 1.5,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5
                    }}
                  >
                    <Box sx={{ color: theme.palette.primary.main, mt: 0.25 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.primary.light, mb: 0.5 }}>
                        Single Stock Analysis
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: `${theme.palette.primary.light}80` }}>
                        Comparing {selectedTickers[0]} against {industrySector ? `${industrySector} sector` : 'industry'} average. Add more stocks for peer comparison.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Industry Info Message */}
                {!hasIndustryAvg && selectedTickers.length > 1 && (
                  <Box
                    sx={{
                      mb: 2.5,
                      bgcolor: `${theme.palette.warning.main}10`,
                      border: `1px solid ${theme.palette.warning.main}30`,
                      borderRadius: 1.5,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5
                    }}
                  >
                    <Box sx={{ color: theme.palette.warning.main, mt: 0.25 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.warning.main, mb: 0.5 }}>
                        Cross-Industry Comparison
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: `${theme.palette.warning.main}80` }}>
                        The selected stocks are from different industries. Showing average of selected stocks instead of industry benchmark.
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                  {/* Bar Chart */}
                  <Box
                    sx={{
                      bgcolor: theme.palette.surface.main,
                      backdropFilter: 'blur(16px)',
                      border: `1px solid ${theme.palette.border.primary}`,
                      borderRadius: 2,
                      p: 2.5
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: theme.palette.text.primary }}>
                        Relative Benchmarking
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          bgcolor: `${theme.palette.primary.main}20`,
                          color: theme.palette.primary.light,
                          fontSize: '10px',
                          fontWeight: 600,
                          borderRadius: 0.5,
                          letterSpacing: '0.05em'
                        }}
                      >
                        BAR CHART
                      </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={barChartData}>
                        <XAxis
                          dataKey="name"
                          stroke={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'}
                          tick={{ fontSize: 12, fill: theme.palette.text.muted }}
                        />
                        <YAxis
                          stroke={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'}
                          tick={{ fontSize: 12, fill: theme.palette.text.muted }}
                        />
                        <Tooltip
                          formatter={(value, name) => [typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value, name]}
                          contentStyle={{
                            backgroundColor: theme.palette.background.secondary,
                            border: `1px solid ${theme.palette.border.primary}`,
                            borderRadius: '8px',
                            fontSize: "var(--font-size-sm)",
                            color: theme.palette.text.primary
                          }}
                          labelStyle={{
                            color: theme.palette.text.primary
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: '14px', color: theme.palette.text.primary }} />
                        {selectedTickers.map((ticker, idx) => (
                          <Bar key={ticker} dataKey={ticker} fill={colors[idx % colors.length]} radius={[8, 8, 0, 0]} />
                        ))}
                        <Bar dataKey={avgLabel} fill="#10b981" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Radar Chart */}
                  <Box
                    sx={{
                      bgcolor: theme.palette.surface.main,
                      backdropFilter: 'blur(16px)',
                      border: `1px solid ${theme.palette.border.primary}`,
                      borderRadius: 2,
                      p: 2.5
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: theme.palette.text.primary }}>
                        Metric Overlap Radar
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          bgcolor: `${theme.palette.primary.main}20`,
                          color: theme.palette.primary.light,
                          fontSize: '10px',
                          fontWeight: 600,
                          borderRadius: 0.5,
                          letterSpacing: '0.05em'
                        }}
                      >
                        POLAR CHART
                      </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={280}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'} />
                        <PolarAngleAxis
                          dataKey="metric"
                          tick={{ fill: theme.palette.text.muted, fontSize: 12 }}
                        />
                        <PolarRadiusAxis
                          stroke={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}
                          tick={{ fontSize: 11, fill: theme.palette.text.muted }}
                        />
                        <Tooltip
                          formatter={(value, name) => [typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value, name]}
                          contentStyle={{
                            backgroundColor: theme.palette.background.secondary,
                            border: `1px solid ${theme.palette.border.primary}`,
                            borderRadius: '8px',
                            fontSize: "var(--font-size-sm)",
                            color: theme.palette.text.primary
                          }}
                          labelStyle={{
                            color: theme.palette.text.primary
                          }}
                        />
                        {selectedTickers.map((ticker, idx) => (
                          <Radar
                            key={ticker}
                            name={ticker}
                            dataKey={ticker}
                            stroke={colors[idx % colors.length]}
                            fill={colors[idx % colors.length]}
                            fillOpacity={0.3}
                          />
                        ))}
                        <Radar
                          name={avgLabel}
                          dataKey={avgLabel}
                          stroke="#10b981"
                          fill="#10b981"
                          fillOpacity={0.3}
                        />
                        <Legend wrapperStyle={{ fontSize: '14px' }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Numerical Matrix Tab */}
            {activeTab === 1 && (
              <Box>
                {/* Single Stock vs Industry Message */}
                {selectedTickers.length === 1 && hasIndustryAvg && (
                  <Box
                    sx={{
                      mb: 2.5,
                      bgcolor: `${theme.palette.primary.main}10`,
                      border: `1px solid ${theme.palette.primary.main}30`,
                      borderRadius: 1.5,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5
                    }}
                  >
                    <Box sx={{ color: theme.palette.primary.main, mt: 0.25 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.primary.light, mb: 0.5 }}>
                        Single Stock Analysis
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: `${theme.palette.primary.light}80` }}>
                        Comparing {selectedTickers[0]} against {industrySector ? `${industrySector} sector` : 'industry'} average. Add more stocks for peer comparison.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Industry Info Message */}
                {!hasIndustryAvg && selectedTickers.length > 1 && (
                  <Box
                    sx={{
                      mb: 2.5,
                      bgcolor: `${theme.palette.warning.main}10`,
                      border: `1px solid ${theme.palette.warning.main}30`,
                      borderRadius: 1.5,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5
                    }}
                  >
                    <Box sx={{ color: theme.palette.warning.main, mt: 0.25 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.palette.warning.main, mb: 0.5 }}>
                        Cross-Industry Comparison
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: `${theme.palette.warning.main}80` }}>
                        The selected stocks are from different industries. Showing average of selected stocks instead of industry benchmark.
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    bgcolor: theme.palette.surface.main,
                    backdropFilter: 'blur(16px)',
                    border: `1px solid ${theme.palette.border.primary}`,
                    borderRadius: 2,
                    p: 2.5,
                    overflowX: 'auto'
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.palette.border.primary}` }}>
                        <th style={{ textAlign: 'left', padding: '12px', color: theme.palette.text.muted, fontWeight: 600, fontSize: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                          METRIC
                        </th>
                        {hasIndustryAvg && (
                          <th style={{ textAlign: 'center', padding: '12px', color: theme.palette.success.main, fontWeight: 600, fontSize: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                            INDUSTRY AVG
                          </th>
                        )}
                        {selectedTickers.map((ticker, idx) => (
                          <th
                            key={ticker}
                            style={{ textAlign: 'center', padding: '12px', fontWeight: 600, fontSize: '12px', letterSpacing: '0.05em', textTransform: 'uppercase', color: colors[idx % colors.length] }}
                          >
                            {ticker}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.map((metric) => (
                        <tr key={metric.key} style={{ borderBottom: `1px solid ${theme.palette.border.primary}50` }}>
                          <td style={{ padding: '12px', fontWeight: 500, fontSize: '14px', color: theme.palette.text.primary }}>{metric.label}</td>
                          {hasIndustryAvg && (
                            <td style={{ textAlign: 'center', padding: '12px', color: theme.palette.success.main, fontWeight: 600, fontSize: '14px' }}>
                              {industryAvg[metric.key] != null ? industryAvg[metric.key].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                            </td>
                          )}
                          {selectedTickers.map((ticker) => {
                            const value = companyData[ticker]?.[metric.key] || 0;
                            const avg = industryAvg[metric.key] || 0;
                            const isHigherBetter = metric.key !== 'peRatio' && metric.key !== 'debtEquity';
                            const isGood = isHigherBetter ? value > avg : value < avg;

                            return (
                              <td key={ticker} style={{ textAlign: 'center', padding: '12px', fontWeight: 600, fontSize: '14px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, color: theme.palette.text.primary }}>
                                  {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  {hasIndustryAvg && avg > 0 && (
                                    isGood ? (
                                      <TrendingUp size={14} color={theme.palette.success.main} />
                                    ) : (
                                      <TrendingDown size={14} color={theme.palette.error.main} />
                                    )
                                  )}
                                </Box>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}

            {/* Narrative Section */}
            <Box
              sx={{
                mt: 3,
                bgcolor: theme.palette.surface.main,
                backdropFilter: 'blur(16px)',
                border: `1px solid ${theme.palette.border.primary}`,
                borderRadius: 2,
                p: 3
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: "var(--font-size-lg)", fontWeight: "var(--font-weight-semibold)", color: theme.palette.text.primary }}>
                  FA Analysis
                </Typography>

                {narrativeLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: theme.palette.text.muted }}>
                    <Loader2 size={14} className="animate-spin" />
                    <Typography sx={{ fontSize: '12px' }}>Generating insight...</Typography>
                  </Box>
                )}
              </Box>

              {!narrativeLoading && narrative && (
                <TypingNarrative text={narrative} speed={6} />
              )}

              {!narrativeLoading && !narrative && (
                <Typography sx={{ color: theme.palette.text.muted, fontSize: '12px' }}>
                  No narrative available.
                </Typography>
              )}
            </Box>
          </>
        )}

        {redirecting && (
          <TimeoutSnackbar open={redirecting} countdown={countdown} />
        )}

        <style>
          {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 0.8s linear infinite;
          }
        `}
        </style>

      </>)}
    </Box>
  );
};

export default Comparison;